import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Avatar,
  Button,
  Input,
  Stack,
  Heading,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  IconButton,
  Badge,
  useColorModeValue,
  Divider,
  Tooltip,
  useToast,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Container,
  SimpleGrid,
  Icon,
  Textarea,
  List,
  ListItem,
  ListIcon,
  Spacer,
  StackDivider,
} from "@chakra-ui/react";
import {
  EditIcon,
  DeleteIcon,
  ArrowBackIcon,
  SettingsIcon,
  LockIcon,
} from "@chakra-ui/icons";
import {
  FaUser,
  FaFileInvoiceDollar,
  FaHistory,
  FaDollarSign,
  FaBell,
  FaPhoneAlt,
  FaEnvelope,
  FaTag,
  FaStickyNote,
  FaRegClock,
  FaCheckCircle,
} from "react-icons/fa";
import { useUser } from "../context/UserContext";
import { useNavigate } from "react-router-dom";

const MyProfile = () => {
  const { loggedInUser, setLoggedInUser } = useUser();
  const [editMode, setEditMode] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("My Profile");
  const navigate = useNavigate();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  // Color mode values
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const menuHoverBg = useColorModeValue("orange.50", "orange.900");
  const menuActiveBg = useColorModeValue("orange.100", "orange.800");
  const textColor = useColorModeValue("gray.800", "white");
  const subTextColor = useColorModeValue("gray.600", "gray.400");

  const [form, setForm] = useState({
    name: loggedInUser?.name || "",
    email: loggedInUser?.email || "",
    phone: loggedInUser?.phone || "",
    password: "",
    role: loggedInUser?.role || "Employee",
    country: loggedInUser?.country || "India",
    department: loggedInUser?.department || "Engineering",
    joinDate: loggedInUser?.joinDate || "2025-01-01",
    employeeId: loggedInUser?.employeeId || "EMP001",
    emergencyContact: loggedInUser?.emergencyContact || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    setLoggedInUser({
      ...loggedInUser,
      name: form.name,
      email: form.email,
      phone: form.phone,
    });
    setEditMode(false);
  };

  const menuItems = [
    { label: "My Profile", icon: FaUser },
    { label: "Payslips", icon: FaFileInvoiceDollar },
    { label: "Leave History", icon: FaHistory },
    { label: "Salary Structure", icon: FaDollarSign },
    { label: "Settings", icon: SettingsIcon },
    { label: "Notifications", icon: FaBell },
  ];

  const handleUploadPhoto = () => {
    // Implement photo upload logic
    setIsUploading(true);
    // Simulating upload
    setTimeout(() => {
      setIsUploading(false);
      toast({
        title: "Profile photo updated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }, 1500);
  };

  // CRM-style local state: activities, notes, tags
  const [activities, setActivities] = useState([
    {
      id: 1,
      type: "call",
      title: "Called merchant about pending invoice",
      time: "2 hours ago",
      done: false,
    },
    {
      id: 2,
      type: "email",
      title: "Sent onboarding guide",
      time: "1 day ago",
      done: true,
    },
  ]);

  const [notes, setNotes] = useState([
    { id: 1, text: "Prefers monthly reports on 1st.", createdAt: "3 days ago" },
  ]);

  const [newNote, setNewNote] = useState("");
  const [tags, setTags] = useState(["VIP", "Retail"]);

  const addNote = () => {
    if (!newNote.trim()) return;
    const note = {
      id: Date.now(),
      text: newNote.trim(),
      createdAt: "just now",
    };
    setNotes((s) => [note, ...s]);
    setNewNote("");
    toast({ title: "Note added", status: "success", duration: 2000 });
  };

  const toggleActivityDone = (id) => {
    setActivities((s) =>
      s.map((a) => (a.id === id ? { ...a, done: !a.done } : a))
    );
  };

  const addTag = (tag) => {
    if (!tag) return;
    if (tags.includes(tag)) return;
    setTags((t) => [...t, tag]);
  };

  return (
    <Flex bg={useColorModeValue("gray.50", "gray.900")} minH="100vh">
      {/* Sidebar */}
      <Box
        w="250px"
        bg={bgColor}
        borderRight="1px"
        borderColor={borderColor}
        pt={6}
        px={4}
        position="sticky"
        top={0}
        h="100vh"
      >
        <Flex align="center" mb={8}>
          <IconButton
            icon={<ArrowBackIcon />}
            onClick={() => navigate("/")}
            variant="ghost"
            aria-label="Back"
            mr={2}
          />
          <Text fontSize="lg" fontWeight="bold" color={textColor}>
            Profile Settings
          </Text>
        </Flex>

        <VStack spacing={2} align="stretch">
          {menuItems.map((item) => (
            <Box
              key={item.label}
              px={4}
              py={3}
              cursor="pointer"
              bg={selectedMenu === item.label ? menuActiveBg : "transparent"}
              color={selectedMenu === item.label ? "orange.500" : textColor}
              fontWeight={selectedMenu === item.label ? "semibold" : "normal"}
              borderRadius="lg"
              transition="all 0.2s"
              _hover={{
                bg: menuHoverBg,
                transform: "translateX(2px)",
              }}
              onClick={() => setSelectedMenu(item.label)}
            >
              <Flex align="center" justify="space-between">
                <HStack spacing={3}>
                  <Icon as={item.icon} />
                  <Text>{item.label}</Text>
                </HStack>
                {item.label === "Notifications" && (
                  <Badge colorScheme="green" borderRadius="full">
                    3
                  </Badge>
                )}
              </Flex>
            </Box>
          ))}
        </VStack>
      </Box>

      {/* Main Content */}
      <Box flex="1" p={8}>
        <Container maxW="container.xl">
          <Flex direction="column">
            {/* Profile Header */}
            <Flex
              bg={bgColor}
              p={6}
              borderRadius="xl"
              shadow="sm"
              mb={6}
              align="center"
              justify="space-between"
              borderBottom="3px solid"
              borderColor="orange.500"
            >
              <HStack spacing={6}>
                <Box position="relative">
                  <Avatar
                    name={form.name}
                    size="xl"
                    src={loggedInUser?.avatar}
                    border="3px solid"
                    borderColor="orange.500"
                  />
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="orange"
                    isLoading={isUploading}
                    onClick={handleUploadPhoto}
                    position="absolute"
                    bottom={0}
                    right={0}
                    rounded="full"
                    aria-label="Change photo"
                  />
                </Box>
                <Box>
                  <Heading size="lg" color={textColor}>
                    {form.name}
                  </Heading>
                  <Text color={subTextColor} fontSize="md">
                    {form.role} • {form.department}
                  </Text>
                  <HStack spacing={4} mt={2}>
                    <Badge colorScheme="green">Active</Badge>
                    <Badge colorScheme="blue">ID: {form.employeeId}</Badge>
                  </HStack>
                </Box>
              </HStack>
              {!editMode ? (
                <Button
                  leftIcon={<EditIcon />}
                  colorScheme="orange"
                  onClick={() => setEditMode(true)}
                  size="lg"
                >
                  Edit Profile
                </Button>
              ) : (
                <HStack>
                  <Button variant="ghost" onClick={() => setEditMode(false)}>
                    Cancel
                  </Button>
                  <Button colorScheme="orange" onClick={handleSave}>
                    Save Changes
                  </Button>
                </HStack>
              )}
            </Flex>

            {/* Tabs Section */}
            <Box bg={bgColor} borderRadius="xl" shadow="sm">
              <Tabs
                colorScheme="orange"
                onChange={setActiveTab}
                value={activeTab}
                variant="enclosed"
              >
                <TabList px={4}>
                  <Tab>Personal Info</Tab>
                  <Tab>Employment</Tab>
                  <Tab>Security</Tab>
                </TabList>

                <TabPanels>
                  {/* Personal Info Tab */}
                  <TabPanel>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                      <FormControl>
                        <FormLabel>Full Name</FormLabel>
                        <Input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          isDisabled={!editMode}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Email Address</FormLabel>
                        <Input
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          isDisabled={!editMode}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Phone Number</FormLabel>
                        <Input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          isDisabled={!editMode}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Country</FormLabel>
                        <Input
                          name="country"
                          value={form.country}
                          onChange={handleChange}
                          isDisabled={!editMode}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Emergency Contact</FormLabel>
                        <Input
                          name="emergencyContact"
                          value={form.emergencyContact}
                          onChange={handleChange}
                          isDisabled={!editMode}
                        />
                      </FormControl>
                    </SimpleGrid>
                  </TabPanel>

                  {/* Employment Tab */}
                  <TabPanel>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                      <FormControl>
                        <FormLabel>Employee ID</FormLabel>
                        <Input value={form.employeeId} isReadOnly />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Department</FormLabel>
                        <Input value={form.department} isReadOnly />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Role</FormLabel>
                        <Input value={form.role} isReadOnly />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Join Date</FormLabel>
                        <Input value={form.joinDate} isReadOnly />
                      </FormControl>
                    </SimpleGrid>
                  </TabPanel>

                  {/* Security Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      <FormControl>
                        <FormLabel>Current Password</FormLabel>
                        <Input type="password" isDisabled={!editMode} />
                      </FormControl>

                      <FormControl>
                        <FormLabel>New Password</FormLabel>
                        <Input
                          type="password"
                          name="password"
                          onChange={handleChange}
                          isDisabled={!editMode}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Confirm New Password</FormLabel>
                        <Input type="password" isDisabled={!editMode} />
                      </FormControl>

                      <Button
                        leftIcon={<LockIcon />}
                        colorScheme="orange"
                        isDisabled={!editMode}
                      >
                        Update Password
                      </Button>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* CRM Right Panel */}
      <Box
        w={{ base: "100%", md: "360px" }}
        p={6}
        bg={useColorModeValue("white", "gray.800")}
        borderLeft="1px"
        borderColor={borderColor}
        position="sticky"
        top={0}
        h="100vh"
        overflowY="auto"
      >
        <VStack align="stretch" spacing={6}>
          {/* Quick Actions */}
          <Box
            p={4}
            borderRadius="lg"
            bg={useColorModeValue("gray.50", "gray.700")}
          >
            <Heading size="sm" mb={3}>
              Quick Actions
            </Heading>
            <HStack spacing={3} wrap="wrap">
              <Button size="sm" colorScheme="orange">
                Message
              </Button>
              <Button size="sm" variant="outline">
                Call
              </Button>
              <Button size="sm" variant="ghost">
                Schedule
              </Button>
              <Button size="sm" variant="ghost">
                Assign
              </Button>
            </HStack>
          </Box>

          {/* Contact Card */}
          <Box p={4} borderRadius="lg" bg={bgColor} boxShadow="sm">
            <Heading size="sm" mb={3}>
              Contact
            </Heading>
            <VStack align="start" spacing={2} divider={<StackDivider />}>
              <HStack>
                <Icon as={FaPhoneAlt} />
                <Text fontSize="sm">{form.phone || "—"}</Text>
              </HStack>
              <HStack>
                <Icon as={FaEnvelope} />
                <Text fontSize="sm">{form.email || "—"}</Text>
              </HStack>
              <HStack>
                <Icon as={FaRegClock} />
                <Text fontSize="sm">Joined {form.joinDate}</Text>
              </HStack>
            </VStack>
          </Box>

          {/* Tags */}
          <Box
            p={4}
            borderRadius="lg"
            bg={useColorModeValue("gray.50", "gray.700")}
          >
            <Heading size="sm" mb={3}>
              Tags
            </Heading>
            <HStack spacing={2} wrap="wrap">
              {tags.map((t) => (
                <Badge
                  key={t}
                  colorScheme="purple"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  {t}
                </Badge>
              ))}
              <Input
                placeholder="Add tag"
                size="sm"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    addTag(e.target.value);
                    e.target.value = "";
                  }
                }}
              />
            </HStack>
          </Box>

          {/* Activity Feed */}
          <Box p={4} borderRadius="lg" bg={bgColor} boxShadow="sm">
            <Heading size="sm" mb={3}>
              Activity Feed
            </Heading>
            <List spacing={3}>
              {activities.map((a) => (
                <ListItem key={a.id}>
                  <HStack justify="space-between">
                    <HStack>
                      <ListIcon
                        as={FaCheckCircle}
                        color={a.done ? "green.400" : "gray.400"}
                      />
                      <Box>
                        <Text
                          fontSize="sm"
                          textDecoration={a.done ? "line-through" : "none"}
                        >
                          {a.title}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {a.time}
                        </Text>
                      </Box>
                    </HStack>
                    <Button size="xs" onClick={() => toggleActivityDone(a.id)}>
                      {a.done ? "Undo" : "Done"}
                    </Button>
                  </HStack>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Notes */}
          <Box
            p={4}
            borderRadius="lg"
            bg={useColorModeValue("gray.50", "gray.700")}
          >
            <Heading size="sm" mb={3}>
              Notes
            </Heading>
            <VStack spacing={3} align="stretch">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a note..."
                size="sm"
              />
              <HStack>
                <Button size="sm" colorScheme="orange" onClick={addNote}>
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setNewNote("")}
                >
                  Clear
                </Button>
              </HStack>

              <Divider />
              <VStack align="stretch" spacing={2}>
                {notes.map((n) => (
                  <Box
                    key={n.id}
                    p={2}
                    borderRadius="md"
                    bg={useColorModeValue("white", "gray.700")}
                  >
                    <Text fontSize="sm">{n.text}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {n.createdAt}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
};

export default MyProfile;
